import * as THREE from 'three';
import { ViewOrientationMaterialFactory } from '../../material/ViewOrientationMaterialFactory';
import { getStandardViewDefinition } from './standardViewDefinitions';
import { StandardViewType, ViewOrientationTargetKind } from './standardViewTypes';
import { ViewOrientationElementMesh } from './ViewOrientationElementMesh';

/**
 * 背面大面交互代理 mesh。
 *
 * 设计说明：
 * 1. 该类只服务于背面大面的 hover / click 代理，不参与 edge / corner 交互；
 * 2. 代理面复用原始大面的 viewType，保证现有标准视图切换链无需改业务语义；
 * 3. 代理面的偏移距离由容器层按相机方向动态驱动，类内部只负责承载几何与偏移更新。
 */
export class ViewOrientationFaceProxyMesh extends ViewOrientationElementMesh {
  /** 代理面初始位置。 */
  private readonly basePosition: THREE.Vector3;

  /** 代理面法线方向。 */
  private readonly normalDirection: THREE.Vector3;

  /**
   * 构造背面大面代理 mesh。
   *
   * @param viewType 标准视图类型。
   * @param sourceFace 原始大面 mesh。
   * @throws 当视图定义不是主面类型时抛出错误。
   */
  public constructor(viewType: StandardViewType, sourceFace: ViewOrientationElementMesh) {
    const definition = getStandardViewDefinition(viewType);
    if (definition.kind !== ViewOrientationTargetKind.Face) {
      throw new Error(`代理面仅支持大面类型：${viewType}`);
    }

    const material = ViewOrientationMaterialFactory.createBasicMaterial(definition.kind);
    material.opacity = 0;
    material.transparent = true;
    material.depthWrite = false;

    super({
      viewType,
      kind: definition.kind,
      label: `${definition.label}-proxy`,
      geometry: sourceFace.geometry.clone(),
      material,
      renderOrder: ViewOrientationElementMesh.RENDER_ORDER - 1,
    });

    this.name = `view-orientation-face-proxy-${viewType}`;
    this.position.copy(sourceFace.position);
    this.quaternion.copy(sourceFace.quaternion);
    this.basePosition = sourceFace.position.clone();
    this.normalDirection = new THREE.Vector3(...definition.direction).normalize();
    this.visible = false;
  }

  /**
   * 设置代理面沿法线方向的偏移距离。
   *
   * @param distance 偏移距离。
   */
  public setOffsetDistance(distance: number): void {
    this.position.copy(this.basePosition).addScaledVector(this.normalDirection, distance);
  }

  /**
   * 释放资源。
   */
  public dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}
